<template>
  <Suspense>
    <!-- component with nested async dependencies -->
    <NuxtLayout>
      <NuxtPage />
      <ClientOnly>
        <Toast />
      </ClientOnly>
    </NuxtLayout>

    <!-- loading state via #fallback slot -->
    <template #fallback> Loading... </template>
  </Suspense>
</template>

<script setup>
import "@/assets/primeVueTheme/themes/mytheme/theme.scss";

import { useRoute } from "vue-router";

import * as platform from "platform";

import { useBrandingStore } from "~/stores/branding";
import { useCartStore } from "~/stores/cart";
import { useSessionStore } from "~/stores/session";
import Network from "~/utils/network";
import { useBnplApplicationStore } from "~/stores/bnplapplication";
import { useUserStore } from "~/stores/user";
import { useSelfcareStore } from "~/stores/selfcare";
import {
  loadAllMessages,
  loadPublicMessages,
  setI18nLanguage,
  setFormats,
} from "~/utils/i18n";
import { addFacebookPixel } from "~/utils/tracking";
import { i18nInstance } from "~/plugins/i18n";
import { usePrescoreStore } from "./stores/prescore";
import { useB2bPrescoreStore } from "./stores/b2bPrescore";
import { useCountryStore } from "./stores/country";
import { useFingerprintStore } from "./stores/fingerprint";
import { usePixelStore } from "./stores/pixel";
import { useReferralStore } from "./stores/referral";
import { useB2bFlowStore } from "./stores/b2bFlow";
import { ofetch } from "ofetch";
import { saveLandingParams } from "~/utils/landingParamUtil";

const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const brandingStore = useBrandingStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const sessionStore = useSessionStore();
const bnplApplicationStore = useBnplApplicationStore();
const b2bFlowStore = useB2bFlowStore();
const selfcareStore = useSelfcareStore();
const prescoreStore =
  route.name === "b2b-financier-prescore"
    ? useB2bPrescoreStore()
    : usePrescoreStore();
const countryStore = useCountryStore();
const fingerprintStore = useFingerprintStore();
const pixelStore = usePixelStore();
const referralStore = useReferralStore();

const Base64 = {
  encode(string) {
    return window.btoa(string);
  },
  decode(string) {
    return window.atob(string);
  },
};

onMounted(async () => {
  try {
    if (process.client && route.params.financier) {
      await pixelStore.fetchFacebookPixelId(route.params.financier);
      // only fires once cookies are accepted
      addFacebookPixel(pixelStore.pixelId);
    }
  } catch {
    console.error("Couldn't get facebook pixel id");
  }
});

// Tech data is saved once per publicId, in the background - the flow never waits for it.
// Every save rewrites the application row, so the saves go one at a time instead of in
// parallel, and they are not repeated on each step change: each repeat could collide with
// the next-step request on the same row.
const techDataInProgress = new Set();

async function saveTechData(store, storageKey, publicId) {
  const savedTechData = JSON.parse(localStorage.getItem(storageKey)) ?? {};
  if (savedTechData[publicId] || techDataInProgress.has(publicId)) {
    return;
  }
  techDataInProgress.add(publicId);
  try {
    await fingerprintStore.load();
    await store.saveDynamicData({
      key: "techDataFingerprintId",
      value: Base64.encode(fingerprintStore.visitorId + platform.description),
    });
    await store.saveDynamicData({
      key: "techDataUserAgent",
      value: platform.description,
    });
    // A failed IP lookup (e.g. blocked by an ad blocker) usually fails the same way on
    // every try, so it does not keep the publicId pending.
    try {
      const ipAndGeo = await ofetch(
        runtimeConfig.public.instacashHelperBaseUrl + "/get-ip"
      );
      await store.saveDynamicData({
        key: "techDataIpAndGeo",
        value: JSON.parse(ipAndGeo),
      });
    } catch {
      console.error("Couldn't get IP");
    }
    const latestSavedTechData =
      JSON.parse(localStorage.getItem(storageKey)) ?? {};
    latestSavedTechData[publicId] = true;
    localStorage.setItem(storageKey, JSON.stringify(latestSavedTechData));
  } catch {
    console.error("Couldn't save tech data");
  } finally {
    techDataInProgress.delete(publicId);
  }
}

watch(
  [
    () => bnplApplicationStore.publicId,
    () => sessionStore.isLoggedIn,
    () => bnplApplicationStore.bnplApplication.currentFlowIndex,
  ],
  ([publicId, isLoggedIn, currentFlowIndex]) => {
    if (process.client && publicId && isLoggedIn && currentFlowIndex) {
      saveTechData(bnplApplicationStore, "saved-tech-data", publicId);
    }
  }
);

// Same for B2B applications
watch(
  [
    () => b2bFlowStore.publicId,
    () => sessionStore.isLoggedIn,
    () => b2bFlowStore.bnplApplication.currentFlowIndex,
  ],
  ([publicId, isLoggedIn, currentFlowIndex]) => {
    if (process.client && publicId && isLoggedIn && currentFlowIndex) {
      saveTechData(b2bFlowStore, "saved-b2b-tech-data", publicId);
    }
  }
);

/* promise.all */
onServerPrefetch(async () => {
  const brandingUrl =
    process?.env?.BRANDING_URL ||
    "http://localhost:3000/api/administration/branding/external/";
  const cartUrl =
    process?.env?.CART_URL || "http://localhost:3000/api/bnpl/external/cart/";
  const userUrl =
    process?.env?.USER_URL || "http://localhost:3000/api/bnpl/external/user/";
  const countryUrl =
    process?.env?.COUNTRY_URL ||
    "http://localhost:3000/api/administration/external/country/financier/";
  const countryByIdUrl =
    process?.env?.COUNTRY_BY_ID_URL ||
    "http://localhost:3000/api/administration/external/country/";
  const referralUrl =
    process?.env?.REFERRAL_URL ||
    "http://localhost:3000/api/customer/mgm/referrer/campaign/";
  const getCampaignByIdUrl =
    process?.env?.CAMPAIGN_BY_ID_URL ||
    "http://localhost:3000/api/customer/external/campaign/";

  if (route.params.financier) {
    const country = await $fetch(countryUrl + route.params.financier);
    countryStore.load(country);
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);
  } else if (route.query.code) {
    const landingPageData = await $fetch(referralUrl + route.query.code);
    referralStore.loadLandingPageData(landingPageData);
    countryStore.load(referralStore.getLandingPageCountry);
    loadPublicMessages(i18nInstance, countryStore.locale);
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);
  } else if (route.params.campaignId) {
    let landingPageData = await $fetch(
      getCampaignByIdUrl + route.params.campaignId
    );
    const campaignOverview = await $fetch(
      getCampaignByIdUrl + "overview/" + route.params.campaignId
    );
    landingPageData = { ...landingPageData, ...campaignOverview };
    const country = await $fetch(countryByIdUrl + landingPageData.countryId);
    referralStore.loadLandingPageData(landingPageData);
    countryStore.load(country);
    loadPublicMessages(i18nInstance, countryStore.locale);
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);
  } else {
    console.log("No financier");
  }

  console.log(route.name);

  if (
    route.name === "financier-publicId" ||
    route.name === "financier" ||
    route.name === "financier-prescore" ||
    route.name === "financier-prescore-prescoreId" ||
    route.name === "financier-publicId-review" ||
    route.name === "financier-test" ||
    /^financier-.*/g.test(route.name) ||
    /^b2b-financier(?:-\w+)+$/g.test(route.name) ||
    /^kyc-financier(?:-\w+)+$/g.test(route.name) ||
    /^kyc-financier-publicId(?:-\w+)+$/g.test(route.name)
  ) {
    /* Get branding */
    try {
      console.log(
        "Fetching branding at " + brandingUrl + route.params.financier
      );
      const colors = await $fetch(brandingUrl + route.params.financier);
      await brandingStore.load(colors);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "Branding could not be loaded - " + e.statusMessage,
      });
    }
  }

  if (
    route.name === "financier-publicId" ||
    route.name === "b2b-financier-publicId" ||
    /^kyc-financier-publicId(?:-\w+)?$/g.test(route.name)
  ) {
    /* Get cart */
    try {
      console.log("Fetching cart at " + cartUrl + route.params.publicId);
      const cart = await $fetch(cartUrl + route.params.publicId);
      await cartStore.load(cart);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "Cart could not be loaded - " + e.statusMessage,
      });
    }

    /* Get user info */
    try {
      console.log("Fetching userInfo at " + userUrl + route.params.publicId);
      const userInfo = await $fetch(userUrl + route.params.publicId);
      await userStore.load(userInfo);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "User info could not be loaded - " + e.statusMessage,
      });
    }
  }

  if (route.name === "financier-publicId-review") {
    /* Get user info */
    try {
      console.log("Fetching userInfo at " + userUrl + route.params.publicId);
      const userInfo = await $fetch(userUrl + route.params.publicId);
      await userStore.load(userInfo);
    } catch (e) {
      console.log(e);
      throw createError({
        statusCode: e.statusCode,
        statusMessage: "User info could not be loaded - " + e.statusMessage,
      });
    }
  }
});
onBeforeMount(() => {
  if (countryStore.locale) {
    useHead({
      htmlAttrs: {
        lang: countryStore.locale,
      },
    });
  }

  if (route.name === "financier-publicId") {
    bnplApplicationStore.publicId = route.params.publicId;
    userStore.publicId = route.params.publicId;
  }
  if (route.name === "b2b-financier-publicId") {
    b2bFlowStore.publicId = route.params.publicId;
    userStore.publicId = route.params.publicId;
  }
  if (route.name === "kyc-financier-publicId") {
    b2bFlowStore.publicId = route.params.publicId;
    userStore.publicId = route.params.publicId;
  }
  if (route.name === "kyc-financier-publicId-login") {
    b2bFlowStore.publicId = route.params.publicId;
    userStore.publicId = route.params.publicId;
  }
  if (route.name === "b2b-financier-prescore-prescoreId") {
    b2bFlowStore.publicId = route.params.prescoreId;
    userStore.publicId = route.params.prescoreId;
  }
  if (["referral", "referral-campaignId"].includes(route.name)) {
    loadPublicMessages(i18nInstance, countryStore.locale);
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);
  }
  if (route.params.financier && countryStore.locale && countryStore.currency) {
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);
  }
  if (
    route.name === "financier-publicId" ||
    route.name === "financier" ||
    route.name === "financier-prescore" ||
    route.name === "financier-prescore-prescoreId" ||
    route.name === "financier-publicId-review" ||
    route.name === "financier-test" ||
    /^financier-.*/g.test(route.name) ||
    /^b2b-financier(?:-\w+)+$/g.test(route.name) ||
    /^kyc-financier(?:-\w+)+$/g.test(route.name) ||
    /^kyc-financier-publicId(?:-\w+)+$/g.test(route.name)
  ) {
    bnplApplicationStore.financier = route.params.financier;
    b2bFlowStore.financier = route.params.financier;
    selfcareStore.financier = route.params.financier;

    if (process.client) {
      sessionStore.initStore(route.params.financier);
      Network.configure(sessionStore);

      if (!sessionStore.isLoggedIn) {
        saveLandingParams(route.query);
      }
    }

    if (brandingStore.logoCompactUrl) {
      useHead({
        link: [
          {
            rel: "icon",
            type: "image/x-icon",
            href: brandingStore.logoCompactUrl,
          },
        ],
      });
    }
  }
  if (
    route.name === "financier-prescore" ||
    route.name === "b2b-financier-prescore"
  ) {
    /* Get prescore parameters */
    let query = route.query;
    let paramsOk = query && query.offerId && query.merchantId;
    if (query.description && query.description.length > 255) {
      query.description = query.description.slice(0, 252) + "...";
    }
    const financier =
      route.name === "b2b-financier-prescore"
        ? "b2b-" + route.params.financier
        : route.params.financier;

    let queryByFinancier = JSON.parse(localStorage.getItem("prescore_query"));

    if (!paramsOk) {
      // try from localStorage
      if (queryByFinancier && queryByFinancier[financier]) {
        query = queryByFinancier[financier];
        paramsOk = query && query.offerId && query.merchantId;
      }
    } else {
      // save to localStorage
      if (queryByFinancier) {
        queryByFinancier[financier] = query;
      } else {
        queryByFinancier = {};
        queryByFinancier[financier] = query;
      }
      localStorage.setItem("prescore_query", JSON.stringify(queryByFinancier));
    }

    if (paramsOk) {
      cartStore.load({
        created: new Date().toLocaleDateString(),
        items: [],
        name: query.name,
        description: query.description,
        sum: query.amount,
      });
      prescoreStore.offerId = query.offerId;
      prescoreStore.redirectUrl = query.redirectUrl;
      prescoreStore.merchantId = query.merchantId;

      /* Get offer */
      try {
        prescoreStore.getOffer(query.offerId);
      } catch (e) {
        showError({
          statusCode: 404,
          statusMessage: "Wrong offer ID",
        });
      }
    } else {
      showError({
        statusCode: 400,
        statusMessage: "Missing required parameters",
      });
    }
  }
});

watch(
  [() => sessionStore.isLoggedIn, () => route.params.financier],
  () => {
    setI18nLanguage(i18nInstance, countryStore.locale + "!");
    setFormats(i18nInstance, countryStore.locale, countryStore.currency);

    if (route.params.financier) {
      if (sessionStore.isLoggedIn) {
        loadAllMessages(i18nInstance, countryStore.locale);
      } else {
        loadPublicMessages(i18nInstance, countryStore.locale);
      }
    }
  },
  { immediate: true }
);
</script>
<style></style>
